import type { CartItem as CartItemType } from '@frontend/libs/types/cart'

import useCart from '@frontend/libs/hooks/useCart'
import { useCartStore } from '@frontend/libs/stores/useCartStore'
import { useConfirmationDialog } from '@shared/features/confirm-modal/ConfirmationDialog'
import { __ } from '@wordpress/i18n'
import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'

export const useCartItem = (item: CartItemType) => {
  const { setCart } = useCartStore((state) => state)
  const {
    error: updateError,
    isLoading: isUpdating,
    updateItem,
  } = useCart().update(item.key)
  const {
    error: removeError,
    isLoading: isRemoving,
    removeItem,
  } = useCart().remove(item.key)
  const [quantity, setQuantity] = useState(item.quantity)

  // eslint-disable-next-line no-undef
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)

  const { getConfirmation } = useConfirmationDialog()

  const handleRemoveItem = useCallback(async () => {
    const confirmed = await getConfirmation({
      body: __('Are you sure you want to remove this item?', 'asavagroup'),
      confirmButtonProps: {
        color: 'danger',
      },
      confirmText: __('Remove', 'asavagroup'),
      dismissButtonProps: {
        color: 'default',
      },
      dismissText: __('Cancel', 'asavagroup'),
      header: __('Remove item', 'asavagroup'),
    })

    if (!confirmed) {
      return
    }

    await toast
      .promise(removeItem(), {
        error:
          removeError?.message || __('Failed to remove item', 'asavagroup'),
        loading: __('Removing...', 'asavagroup'),
        success: __('Item removed successfully', 'asavagroup'),
      })
      .then((res) => {
        setCart(res)
      })
  }, [getConfirmation, removeError, removeItem, setCart])

  const handleUpdateItem = useCallback(
    async (value: number) => {
      setQuantity(value)

      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      const id = setTimeout(async () => {
        await toast
          .promise(updateItem({ quantity: value }), {
            error:
              updateError?.message || __('Failed to update item', 'asavagroup'),
            loading: __('Updating...', 'asavagroup'),
            success: __('Item updated successfully', 'asavagroup'),
          })
          .then((res) => {
            setCart(res)
          })
      }, 500)

      setTimeoutId(id)
    },
    [timeoutId, updateItem, updateError?.message, setCart],
  )

  return {
    handleRemoveItem,
    handleUpdateItem,
    isLoading: isUpdating || isRemoving,
    quantity,
  }
}

export default useCartItem
